// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Priority Badge
// Visual indicator for prescription urgency
// ============================================================================

import { clsx } from 'clsx';
import type { Prescription } from '@/types';

type Priority = 'STAT' | 'HIGH' | 'NORMAL' | 'LOW';

interface PriorityBadgeProps {
  priority: Priority;
  className?: string;
}

export function PriorityBadge({ priority, className }: PriorityBadgeProps) {
  const styles = {
    STAT: 'bg-red-600 text-white animate-pulse-urgent',
    HIGH: 'bg-orange-500 text-white',
    NORMAL: 'bg-blue-100 text-blue-800',
    LOW: 'bg-gray-100 text-gray-600',
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase',
        styles[priority],
        className
      )}
    >
      {priority}
    </span>
  );
}

// Utility to determine priority from prescription
export function getPrescriptionPriority(prescription: Prescription): Priority {
  // STAT orders are highest priority
  if (prescription.isStatOrder) {
    return 'STAT';
  }

  // Calculate age in minutes
  const receivedAt = prescription.receivedAt || prescription.createdAt;
  const ageMinutes = (Date.now() - new Date(receivedAt).getTime()) / 60000;

  // Controlled substances get higher priority
  if (prescription.isControlled && ageMinutes > 30) {
    return 'HIGH';
  }

  // Old prescriptions escalate
  if (ageMinutes > 120) {
    return 'HIGH';
  }

  if (ageMinutes > 60) {
    return 'NORMAL';
  }

  return 'LOW';
}
